import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { ProfileService } from "service/ProfileService";
import { LOADING, SUCCEEDED, FAILED, IDLE } from "constants/fetch";

export interface VerificationStatusData {
    /** none | action | pending | rejected | verified */
    status: string;
    /** Причина отклонения от модератора */
    comment: string | null;
    requested_at: string | null;
    reviewed_at: string | null;
    /** Бэкенд разрешает отправить заявку прямо сейчас */
    can_request: boolean;
}

interface VerificationState {
    data: VerificationStatusData | null;
    status: string;
    /** Статусом управляет только владелец компании — остальным бэкенд отвечает 403 */
    isForbidden: boolean;
    error: unknown;
}

const initialState: VerificationState = {
    data: null,
    status: IDLE,
    isForbidden: false,
    error: null,
};

/** Статус проверки компании владельца: GET /v2/profile/verification */
export const getVerificationStatus = createAsyncThunk(
    "verification/get",
    async (_: void, { rejectWithValue }) => {
        try {
            const res = await ProfileService.getVerification();
            return res.data as VerificationStatusData;
        } catch (error: any) {
            return rejectWithValue(error.response?.status || 500);
        }
    },
);

/** Отправка компании на проверку: POST /v2/profile/verification */
export const requestVerification = createAsyncThunk(
    "verification/request",
    async (_: void, { rejectWithValue }) => {
        try {
            const res = await ProfileService.requestVerification();
            return res.data as VerificationStatusData;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const verificationSlice = createSlice({
    name: "verification",
    initialState,
    reducers: {
        removeVerificationData: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getVerificationStatus.pending, (state) => {
                state.status = LOADING;
            })
            .addCase(getVerificationStatus.fulfilled, (state, action) => {
                state.status = SUCCEEDED;
                state.isForbidden = false;
                state.error = null;
                state.data = action.payload;
            })
            .addCase(getVerificationStatus.rejected, (state, action) => {
                state.status = FAILED;
                // 403 — сотрудник, а не владелец; 404 — компании ещё нет. Это не ошибка интерфейса
                state.isForbidden = action.payload === 403 || action.payload === 404;
                state.error = action.payload;
                state.data = null;
            })
            .addCase(requestVerification.fulfilled, (state, action) => {
                state.status = SUCCEEDED;
                state.error = null;
                state.data = action.payload;
            })
            .addCase(requestVerification.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

export const { removeVerificationData } = verificationSlice.actions;

export default verificationSlice.reducer;
